import type { FxReferenceCategory } from '@mobile-mapping/providers';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import { UnreachableCaseError } from 'ts-essentials';

import type { ProjectDataLoadItemType } from './project-data-load-control';
import type {
  FxWorkspaceTreeItem,
  FxWorkspaceTreeItemKind,
} from './fx-workspace-tree.types';
import {
  getProjectDataCategoryRootId,
  MissionTreeNodeData,
  MissionTreeNodeType,
  ProjectDataTreeCategory,
} from './project-data-tree-nodes';

function referenceCategoryId(
  workspaceId: string,
  category: FxReferenceCategory
): string {
  return `${workspaceId}::references::${category}`;
}

function scopedReferenceNodeId(
  workspaceId: string,
  category: ProjectDataTreeCategory,
  nodeId: string
): string {
  return `${workspaceId}::references::${category}::${nodeId}`;
}

function scopedReferenceParentId(
  workspaceId: string,
  category: ProjectDataTreeCategory,
  parentId: string | undefined,
  categoryParentId: string
): string | undefined {
  if (parentId === undefined) {
    return undefined;
  }

  if (parentId === getProjectDataCategoryRootId(category)) {
    return categoryParentId;
  }

  return scopedReferenceNodeId(workspaceId, category, parentId);
}

function getFxReferenceNodeMeta(
  category: ProjectDataTreeCategory,
  node: TreeNodeDefinition<unknown>
): {
  kind: FxWorkspaceTreeItemKind;
  refTrn?: string;
  loadItemType?: ProjectDataLoadItemType;
  showVisibilityToggle?: boolean;
} {
  switch (category) {
    case 'missions': {
      const data = node.data as MissionTreeNodeData | undefined;

      if (data === undefined) {
        return { kind: 'referenceMission', showVisibilityToggle: false };
      }

      switch (data.type) {
        case MissionTreeNodeType.Mission:
          return { kind: 'referenceMission', showVisibilityToggle: false };
        case MissionTreeNodeType.Runs:
          return { kind: 'referenceRuns', showVisibilityToggle: false };
        case MissionTreeNodeType.Run:
          return {
            kind: 'reference',
            refTrn: node.id,
            loadItemType: 'pointCloud',
          };
        case MissionTreeNodeType.Path:
          return {
            kind: 'reference',
            refTrn: node.id,
            loadItemType: 'trajectory',
          };
        default:
          throw new UnreachableCaseError(data.type);
      }
    }
    case 'models':
      if (node.isLeaf === false) {
        return { kind: 'referenceFolder', showVisibilityToggle: false };
      }

      return { kind: 'reference', refTrn: node.id, loadItemType: 'model' };
    case 'pointClouds':
      return { kind: 'reference', refTrn: node.id, loadItemType: 'pointCloud' };
    case 'trajectories':
      return { kind: 'reference', refTrn: node.id, loadItemType: 'trajectory' };
    default:
      throw new UnreachableCaseError(category);
  }
}

export function adaptProjectDataNodesForFxReferences(
  workspaceId: string,
  category: ProjectDataTreeCategory,
  nodes: TreeNodeDefinition<unknown>[]
): TreeNodeDefinition<FxWorkspaceTreeItem>[] {
  const categoryRootId = getProjectDataCategoryRootId(category);
  const categoryParentId = referenceCategoryId(workspaceId, category);

  return nodes
    .filter((node) => node.id !== categoryRootId)
    .map((node) => {
      const { kind, refTrn, loadItemType, showVisibilityToggle } =
        getFxReferenceNodeMeta(category, node);

      return {
        id: scopedReferenceNodeId(workspaceId, category, node.id),
        label: node.label,
        parentId: scopedReferenceParentId(
          workspaceId,
          category,
          node.parentId,
          categoryParentId
        ),
        isLeaf: node.isLeaf,
        labelIcon: node.labelIcon,
        labelSvgIcon: node.labelSvgIcon,
        sortPriority: node.sortPriority,
        toggleTooltip: node.toggleTooltip,
        dataTestId: node.dataTestId,
        showVisibilityToggle,
        data: {
          kind,
          workspaceId,
          category,
          ...(refTrn !== undefined ? { refTrn } : {}),
          ...(loadItemType !== undefined ? { loadItemType } : {}),
        },
      } satisfies TreeNodeDefinition<FxWorkspaceTreeItem>;
    });
}
